import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Bell,
  FilePlus,
  FileText,
  HardDrive,
  Image as ImageIcon,
  Megaphone,
  Pencil,
  RefreshCw,
  Star,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { gallery, media, pages, reviews, testimonials } from '../../services/api/admin';
import { get } from '../../services/api/client';

import CockpitHome from './CockpitHome';
import {
  AdaptiveSubnav,
  CockpitPanel,
  CockpitTabs,
  CockpitWorkspace,
  CockpitWorkspaceContent,
} from './cockpit';
import { adminRoutes } from './navigation/adminRoutes';
import { getRememberedCockpitPath } from './navigation/adminPlaceMemory';
import { canAccessSection, getAdminUser } from './navigation/sectionAccess';

const EMPTY_DATA = {
  announcements: [],
  pages: [],
  gallery: [],
  media: { items: [], total: 0, storageBytes: 0 },
  testimonials: [],
  pendingReviews: [],
};

const DATA_SOURCES = {
  announcements: 'announcements',
  pages: 'custom pages',
  gallery: 'gallery',
  media: 'media library',
  testimonials: 'testimonials',
  pendingReviews: 'pending reviews',
};

export const formatBytes = (bytes = 0) => {
  if (!bytes) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / (1024 ** unitIndex);
  const precision = value >= 10 || unitIndex === 0 ? 0 : 1;

  return `${value.toFixed(precision)} ${units[unitIndex]}`;
};

const loadMediaSummary = async () => {
  const firstPage = await media.list({ page: 1, pageSize: 100 });
  const totalPages = Math.max(firstPage.total_pages || 1, 1);
  const remainingPages = totalPages > 1
    ? await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) => (
        media.list({ page: index + 2, pageSize: 100 })
      ))
    )
    : [];
  const items = [firstPage, ...remainingPages].flatMap((result) => result.items || []);

  return {
    items,
    total: firstPage.total ?? items.length,
    storageBytes: items.reduce((total, item) => total + (item.file_size_bytes || 0), 0),
  };
};

/**
 * Load each KPI independently so a problem in one content area does not blank
 * the whole cockpit. Callers receive both the usable data and failed source ids.
 */
export const loadWebsiteCockpitData = async () => {
  const loaders = {
    announcements: () => get('/admin/announcements'),
    pages: () => pages.getAll(),
    gallery: () => gallery.getAll(),
    media: loadMediaSummary,
    testimonials: () => testimonials.getAll(),
    pendingReviews: () => reviews.getSavedForLater(),
  };
  const entries = Object.entries(loaders);
  const results = await Promise.allSettled(entries.map(([, load]) => load()));

  return results.reduce((summary, result, index) => {
    const key = entries[index][0];
    if (result.status === 'fulfilled') {
      summary.data[key] = result.value || EMPTY_DATA[key];
    } else {
      summary.failed.push(key);
    }
    return summary;
  }, { data: { ...EMPTY_DATA }, failed: [] });
};

const getSubsectionId = (section, search) => {
  if (!section?.subSections?.length) return null;
  const requestedView = new URLSearchParams(search).get('view');
  return section.subSections.some((subsection) => subsection.id === requestedView)
    ? requestedView
    : section.subSections[0].id;
};

const WebsiteCockpitHome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const adminUser = useMemo(() => getAdminUser(), []);
  const hasAccess = useCallback(
    (sectionId) => canAccessSection(sectionId, adminUser),
    [adminUser],
  );
  const sections = useMemo(
    () => adminRoutes.website.sections.filter((section) => hasAccess(section.id)),
    [hasAccess],
  );
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const activeSection = sections.find((section) => section.path === normalizedPath);
  const activeSubsectionId = getSubsectionId(activeSection, location.search);
  const activeSubsection = activeSection?.subSections?.find(
    (subsection) => subsection.id === activeSubsectionId,
  );
  const [data, setData] = useState(EMPTY_DATA);
  const [failed, setFailed] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    const result = await loadWebsiteCockpitData();
    setData(result.data);
    setFailed(result.failed);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const hasFailed = useCallback((...sources) => (
    sources.some((source) => failed.includes(source))
  ), [failed]);
  const displayValue = useCallback((sources, value) => (
    loading || hasFailed(...sources) ? '—' : value
  ), [hasFailed, loading]);
  const displaySubtitle = useCallback((sources, value) => {
    if (loading) return 'Loading…';
    return hasFailed(...sources) ? 'Unavailable' : value;
  }, [hasFailed, loading]);

  const publishedPages = data.pages.filter((page) => page.is_published).length;
  const draftPages = data.pages.length - publishedPages;
  const activeAnnouncements = data.announcements.filter((announcement) => announcement.active);

  const kpis = useMemo(() => [
    {
      id: 'active-announcements',
      title: 'Active ribbons',
      value: displayValue(['announcements'], activeAnnouncements.length),
      subtitle: displaySubtitle(['announcements'], `${data.announcements.length} configured`),
      icon: Bell,
      tone: activeAnnouncements.length > 0 ? 'good' : undefined,
    },
    {
      id: 'custom-pages',
      title: 'Custom pages',
      value: displayValue(['pages'], `${publishedPages} / ${draftPages}`),
      subtitle: displaySubtitle(['pages'], 'Published / draft'),
      icon: FileText,
    },
    {
      id: 'gallery-media',
      title: 'Gallery & media',
      value: displayValue(['gallery', 'media'], `${data.gallery.length} / ${data.media.total}`),
      subtitle: displaySubtitle(
        ['gallery', 'media'],
        `Gallery / library • ${formatBytes(data.media.storageBytes)}`,
      ),
      icon: HardDrive,
    },
    {
      id: 'testimonials',
      title: 'Testimonials',
      value: displayValue(
        ['testimonials', 'pendingReviews'],
        `${data.testimonials.length} / ${data.pendingReviews.length}`,
      ),
      subtitle: displaySubtitle(['testimonials', 'pendingReviews'], 'Visible / pending'),
      icon: Star,
      tone: data.pendingReviews.length > 0 ? 'warn' : undefined,
    },
  ], [
    activeAnnouncements.length,
    data.announcements.length,
    data.gallery.length,
    data.media.storageBytes,
    data.media.total,
    data.pendingReviews.length,
    data.testimonials.length,
    displaySubtitle,
    displayValue,
    draftPages,
    publishedPages,
  ]);

  const quickActions = useMemo(() => [
    {
      id: 'edit-hero',
      label: 'Edit hero',
      icon: Pencil,
      path: '/admin/website/hero',
      sectionId: 'hero',
    },
    {
      id: 'new-page',
      label: 'New page',
      icon: FilePlus,
      path: '/admin/website/pages',
      sectionId: 'pages',
    },
    {
      id: 'manage-announcements',
      label: 'Manage announcements',
      icon: Megaphone,
      path: '/admin/website/announcements',
      sectionId: 'announcements',
    },
    {
      id: 'add-testimonial',
      label: 'Add testimonial',
      icon: Star,
      path: '/admin/business/reviews',
      sectionId: 'testimonials',
    },
  ], []);

  if (!activeSection) {
    return sections[0]
      ? <Navigate to={getRememberedCockpitPath('website', sections, adminUser)} replace />
      : <Navigate to="/admin" replace />;
  }

  const tabPanelId = `website-cockpit-tab-panel-${activeSection.id}`;

  return (
    <CockpitHome
      categoryId="website"
      description="Manage the public website, publishing queue, and customer proof."
      kpis={kpis}
      quickActions={quickActions}
      canAccessSection={hasAccess}
      showSectionLinks={false}
      alerts={failed.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-100">
                  Some website metrics could not be loaded
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Unavailable: {failed.map((source) => DATA_SOURCES[source]).join(', ')}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={loadDashboard} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
      overview={(
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardContent className="p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-red-800 dark:text-red-400" />
                  <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                    Announcement ribbons
                  </h2>
                </div>
                {!loading && !hasFailed('announcements') && (
                  <span className="text-xs text-stone-500 dark:text-stone-400">
                    {activeAnnouncements.length} active
                  </span>
                )}
              </div>

              {loading ? (
                <p className="text-sm text-stone-500 dark:text-stone-400">Loading announcements…</p>
              ) : hasFailed('announcements') ? (
                <p className="text-sm text-stone-500 dark:text-stone-400">
                  Announcement status is temporarily unavailable.
                </p>
              ) : activeAnnouncements.length === 0 ? (
                <p className="text-sm text-stone-500 dark:text-stone-400">
                  No announcement ribbon is live.
                </p>
              ) : (
                <div className="space-y-3">
                  {activeAnnouncements.map((announcement) => (
                    <div
                      key={announcement.id}
                      className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950/30"
                    >
                      <div className="mb-1 flex items-center gap-2">
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
                          Live
                        </span>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {announcement.message}
                        </p>
                      </div>
                      {announcement.cta_text && (
                        <p className="text-xs text-stone-600 dark:text-stone-400">
                          CTA: {announcement.cta_text}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="mb-4 flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-red-800 dark:text-red-400" />
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">Content queue</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-stone-50 p-3 dark:bg-stone-900">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {displayValue(['pages'], draftPages)}
                  </p>
                  <p className="text-sm text-stone-500 dark:text-stone-400">Draft pages</p>
                </div>
                <div className="rounded-lg bg-stone-50 p-3 dark:bg-stone-900">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {displayValue(['pendingReviews'], data.pendingReviews.length)}
                  </p>
                  <p className="text-sm text-stone-500 dark:text-stone-400">Reviews pending</p>
                </div>
                <div className="col-span-2 rounded-lg bg-stone-50 p-3 dark:bg-stone-900">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {displayValue(['media'], formatBytes(data.media.storageBytes))}
                  </p>
                  <p className="text-sm text-stone-500 dark:text-stone-400">
                    Tracked media storage across {displayValue(['media'], data.media.total)} files
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    >
      <CockpitTabs
        tabs={sections}
        active={activeSection.id}
        onChange={(sectionId) => {
          const section = sections.find((candidate) => candidate.id === sectionId);
          if (section) navigate(section.path);
        }}
        label="Website and content sections"
        idPrefix="website-cockpit-tab"
      />

      <CockpitPanel
        id={tabPanelId}
        aria-labelledby={`website-cockpit-tab-${activeSection.id}`}
      >
        {activeSection.subSections?.length ? (
          <CockpitWorkspace>
            <AdaptiveSubnav
              tabs={activeSection.subSections}
              active={activeSubsectionId}
              onChange={(subsectionId) => {
                const subsection = activeSection.subSections.find(
                  (candidate) => candidate.id === subsectionId,
                );
                if (subsection) navigate(subsection.path);
              }}
              label={`${activeSection.label} views`}
              idPrefix={`website-${activeSection.id}-view`}
            />
            <CockpitWorkspaceContent
              role="tabpanel"
              id={`website-${activeSection.id}-view-panel-${activeSubsectionId}`}
              aria-labelledby={`website-${activeSection.id}-view-${activeSubsectionId}`}
            >
              <Outlet context={{ activeSection, activeSubsection }} />
            </CockpitWorkspaceContent>
          </CockpitWorkspace>
        ) : (
          <CockpitWorkspaceContent>
            <Outlet context={{ activeSection, activeSubsection: null }} />
          </CockpitWorkspaceContent>
        )}
      </CockpitPanel>
    </CockpitHome>
  );
};

export default WebsiteCockpitHome;
